package features.payment

import features.booking.SmartVanColors
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import features.booking.BookingViewModel

@Composable
fun SpeiConfirmationScreen(
    bookingViewModel: BookingViewModel,
    paymentViewModel: PaymentViewModel,
    onFinishClick: () -> Unit
) {
    val speiResult by paymentViewModel.speiResult.collectAsState()
    val speiClabe by paymentViewModel.speiReference.collectAsState()
    val pagoConfirmado by paymentViewModel.pagoConfirmado.collectAsState()
    val esPagoManual by paymentViewModel.esPagoManual.collectAsState()
    val telefonoAtencion by paymentViewModel.telefonoAtencionPago.collectAsState()
    val beneficiario by network.ConfigPagos.beneficiarioManual.collectAsState()
    val clipboard = LocalClipboardManager.current

    val totalToPay = speiResult?.monto ?: 0

    val smartvanCyan = SmartVanColors.CyanAccent
    val smartvanGreen = SmartVanColors.Verde

    // Iniciar escucha de confirmación cuando se muestra la pantalla
    LaunchedEffect(speiResult?.paymentIntentId) {
        val piId = speiResult?.paymentIntentId
        if (!piId.isNullOrBlank()) {
            paymentViewModel.escucharConfirmacionPago(piId)
        }
    }

    DisposableEffect(Unit) {
        onDispose { paymentViewModel.detenerEscuchaPago() }
    }

    // FIX #15: Scrollable para pantallas pequeñas
    Column(
        modifier = Modifier.fillMaxSize().padding(24.dp).verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text("Transferencia SPEI", style = MaterialTheme.typography.displaySmall, fontWeight = FontWeight.Black, color = smartvanCyan)
        Spacer(modifier = Modifier.height(16.dp))
        Text("Realiza tu transferencia y asegura tus lugares.", textAlign = TextAlign.Center, color = Color.Gray, modifier = Modifier.padding(top = 8.dp))

        Spacer(modifier = Modifier.height(32.dp))

        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
            shape = RoundedCornerShape(size = 16.dp)
        ) {
            Column(modifier = Modifier.padding(24.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                Text("Banco: ${speiResult?.banco ?: "STP"}", fontWeight = FontWeight.SemiBold, color = Color.DarkGray)
                // Beneficiario según la fuente de la CLABE:
                //  - Manual (Stripe apagado): la CLABE es la cuenta propia del negocio → beneficiario configurado.
                //  - Stripe: la CLABE la genera Stripe (Citi/STP) y cobra a nombre de SmartVan → NO el manual.
                val beneficiarioMostrar = if (esPagoManual) beneficiario.ifBlank { "SMARTVAN" } else "SmartVan"
                Text("Beneficiario: $beneficiarioMostrar", fontWeight = FontWeight.SemiBold, color = Color.DarkGray)

                HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp))

                Text("CLABE INTERBANCARIA", fontWeight = FontWeight.Bold, color = smartvanCyan)
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 8.dp)) {
                    Text(
                        text = speiClabe ?: "Generando...",
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Black,
                        color = smartvanCyan
                    )
                    val clabeVal = speiClabe
                    if (!clabeVal.isNullOrBlank()) {
                        Spacer(modifier = Modifier.width(6.dp))
                        IconButton(onClick = { clipboard.setText(AnnotatedString(clabeVal)) }, modifier = Modifier.size(28.dp)) {
                            Icon(Icons.Filled.ContentCopy, contentDescription = "Copiar CLABE", tint = smartvanCyan, modifier = Modifier.size(18.dp))
                        }
                    }
                }

                if (speiResult?.referencia != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text("Referencia:", fontWeight = FontWeight.SemiBold, color = Color.Gray)
                    Text(
                        speiResult!!.referencia.takeLast(12),
                        fontWeight = FontWeight.Bold,
                        color = Color.DarkGray
                    )
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp))

                Text("Monto exacto a transferir:", fontWeight = FontWeight.SemiBold)
                Text(
                    "$$totalToPay MXN",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = smartvanGreen
                )
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
            shape = RoundedCornerShape(12.dp)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text("Tu asiento queda apartado 1 hora.", fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro)
                Spacer(modifier = Modifier.height(4.dp))
                if (esPagoManual) {
                    Text("Realiza tu transferencia y envía tu comprobante dentro de esa hora. Si no se registra el pago en 1 hora, el asiento se libera automáticamente.", color = SmartVanColors.VerdeOscuro, style = MaterialTheme.typography.bodySmall)
                } else {
                    Text("Completa tu transferencia dentro de esa hora. Al confirmarse el pago recibirás tus boletos por correo. Si no se registra en 1 hora, el asiento se libera automáticamente.", color = SmartVanColors.VerdeOscuro, style = MaterialTheme.typography.bodySmall)
                }
            }
        }

        // Mensaje para enviar comprobante (solo modo manual)
        if (esPagoManual && telefonoAtencion.isNotBlank()) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE3F2FD)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text("Envía tu comprobante de pago", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    Spacer(modifier = Modifier.height(4.dp))
                    Text("Para confirmar tu reserva, envía tu comprobante de transferencia al siguiente número:", color = Color.DarkGray, style = MaterialTheme.typography.bodySmall)
                    Spacer(modifier = Modifier.height(8.dp))
                    androidx.compose.material3.Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.AzulOscuro),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(20.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(telefonoAtencion, color = Color.White, fontWeight = FontWeight.Black, style = MaterialTheme.typography.titleMedium)
                            Spacer(modifier = Modifier.weight(1f))
                            IconButton(onClick = { clipboard.setText(AnnotatedString(telefonoAtencion)) }, modifier = Modifier.size(32.dp)) {
                                Icon(Icons.Filled.ContentCopy, contentDescription = "Copiar teléfono", tint = Color.White, modifier = Modifier.size(18.dp))
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                    Text("Una vez verificado tu pago, recibirás tus boletos por correo electrónico.", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                }
            }
        }

        // Banner de pago confirmado en tiempo real
        AnimatedVisibility(visible = pagoConfirmado) {
            Card(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Row(
                    modifier = Modifier.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = smartvanGreen, modifier = Modifier.size(28.dp))
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text("Pago confirmado", fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro)
                        Text("Tus boletos fueron enviados a tu correo.", style = MaterialTheme.typography.bodySmall, color = Color(0xFF388E3C))
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(32.dp))

        Button(
            onClick = { onFinishClick() },
            modifier = Modifier.fillMaxWidth().height(50.dp),
            colors = ButtonDefaults.buttonColors(containerColor = if (pagoConfirmado) smartvanGreen else smartvanCyan),
            shape = RoundedCornerShape(12.dp)
        ) {
            Text(if (pagoConfirmado) "Ver mis boletos" else "Entendido", fontWeight = FontWeight.Bold)
        }
    }
}